import {
  Typography,
  Link,
  Grid,
  Button,
  Card,
  CardHeader,
  RatingIcons,
  CardContent,
  Stack,
} from '@packages/shared';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import type { ReactNode } from 'react';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { LazyRender } from '@packages/modules';
import { SlotBox } from '../SlotBox';
import { Review } from './Review';
import { RatingInfo } from './RatingInfo';
import { RatingTags } from './RatingTags';
import { AddRating } from './AddRating';
import { ReviewStatistic } from './ReviewStatistic';
import { CustomerRatingsFallback } from './CustomerRatingsFallback';

export const RatingSeoResultFragment = graphql(/* GraphQL */ `
  fragment RatingSeoResultFragment on RatingResult {
    product {
      avgRating
      reviewCount
      reviews {
        ...ReviewFragment
      }
      tags {
        ...RatingTagsFragment
      }
      reviewStatistics {
        ...ReviewStatisticFragment
      }
      sku
    }
    verifiedRatingsHintLayerId
  }
`);

type CustomerRatingsProps = {
  /**
   * RatingResult product data
   */
  ratingResultData: FragmentType<typeof RatingSeoResultFragment>;
  /**
   * Product overview component displayed in the dialog
   */
  productOverview: ReactNode;
};

/**
 * Customer ratings slot component displayed in the detailview, containing statistics, tag lists for filtering, max 3 reviews and the add rating button
 */
export const CustomerRatings = ({ ratingResultData, productOverview }: CustomerRatingsProps) => {
  const { product, verifiedRatingsHintLayerId } = getFragmentData(
    RatingSeoResultFragment,
    ratingResultData,
  );
  const { formatMessage } = useIntl();
  const { pathname, query } = useRouter();
  if (!product) {
    return null;
  }
  const { avgRating, reviewCount, reviews, tags, reviewStatistics } = product;

  return (
    <>
      <Typography
        variant="h2"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, m: 2, typography: ['h4', 'h4', 'h3'] }}
        component="header"
      >
        {formatMessage({
          id: 'detailview.grid.headline.ratings',
          defaultMessage: 'Kundenbewertungen',
        })}
        <RatingInfo verifiedRatingsHintLayerId={verifiedRatingsHintLayerId} />
      </Typography>
      <SlotBox>
        <LazyRender
          key={`${product.sku}-customer-ratings`}
          fallback={
            <CustomerRatingsFallback
              previewReviewsCount={reviews.length}
              reviewsCount={reviewCount}
            />
          }
          options={{ threshold: 0.1 }}
        >
          <Grid
            container
            columnSpacing={{ xs: 2, sm: 2, md: 2, lg: 5.5 }}
            rowSpacing={1.5}
            id="customer-ratings"
          >
            <Grid
              item
              xs={12}
              md={6}
              lg={5}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              {reviewCount > 0 && (
                <Card>
                  <CardHeader
                    sx={{ borderBottom: 1, borderColor: 'grey.main', py: 1, px: 2 }}
                    title={formatMessage({
                      id: 'ratingsOverview.title',
                      defaultMessage: 'Bewertungsübersicht',
                    })}
                  />
                  <CardContent>
                    <RatingIcons avgRating={avgRating} reviewCount={reviewCount} sx={{ mb: 0.5 }} />
                    <Stack spacing={0.5} mb={3}>
                      {reviewStatistics.map((rs, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <ReviewStatistic reviewStatisticData={rs} key={index} />
                      ))}
                    </Stack>
                    <RatingTags ratingTagsData={tags} />
                  </CardContent>
                </Card>
              )}
              <AddRating productOverview={productOverview} />
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              lg={7}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
            >
              {reviewCount > 0 && (
                <>
                  {reviews.map((review, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Review key={index} reviewData={review} />
                  ))}
                  <Link
                    href={{ pathname: `${pathname}/rating/[[...ftag]]`, query }}
                    sx={{ alignSelf: 'center', mt: ['auto', 'auto', 1.5] }}
                    noLinkStyle
                  >
                    <Button>
                      {formatMessage(
                        {
                          defaultMessage: 'Alle Bewertungen ({ratingCount}) anzeigen',
                          id: 'ratings.show-all',
                        },
                        { ratingCount: reviewCount },
                      )}
                    </Button>
                  </Link>
                </>
              )}
            </Grid>
          </Grid>
        </LazyRender>
      </SlotBox>
    </>
  );
};
